import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { PackageSearch, MapPin, Clock, Check, Truck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAGE_ICONS = { default: Check };

const reveal = {
  hidden: { opacity: 0, y: 44 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 100, damping: 20 },
  },
};

export default function Tracking() {
  const [query, setQuery] = useState("");
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [samples, setSamples] = useState([]);

  const track = useCallback(async (id, silent = false) => {
    const tid = (id || "").trim().toUpperCase();
    if (!tid) {
      toast.warning("Enter a tracking ID first");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/track/${tid}`);
      setShipment(data);
      if (!silent) toast.success(`Order ${data.tracking_id} located — ${data.status}`);
    } catch (e) {
      if (!silent) toast.error(samples.length > 0 ? `Tracking ID "${tid}" not found. Try one of the sample IDs below.` : `Tracking ID "${tid}" not found — check the ID in your order confirmation.`);
      if (!silent) setShipment(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- samples is set once by the mount effect
  }, []);

  useEffect(() => {
    axios.get(`${API}/shipments`).then(({ data }) => {
      setSamples(data);
      const pre = new URLSearchParams(window.location.search).get("track");
      if (pre) {
        setQuery(pre.toUpperCase());
        track(pre, true);
      } else if (data.length > 0) {
        setQuery(data[0].tracking_id);
        track(data[0].tracking_id, true);
      }
    }).catch(() => {});
  }, [track]);

  const pct = shipment ? (shipment.stage_index / (shipment.stages.length - 1)) * 100 : 0;

  return (
    <section id="tracking" data-testid="tracking-section" className="relative py-24 lg:py-32">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(255,159,28,0.05),transparent_55%)]" />
      <div className="relative mx-auto max-w-4xl px-6 lg:px-12">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="text-center"
        >
          <p className="mb-4 flex items-center justify-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-amber-500">
            <span className="h-px w-10 bg-amber-500" />
            Live Tracking
            <span className="h-px w-10 bg-amber-500" />
          </p>
          <h2 className="font-display text-4xl font-medium tracking-tight sm:text-5xl">
            Track Your PAQVERO Order.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-2 lg:text-lg">
            See the status of your PAQVERO order from fulfillment to delivery —
            enter a tracking ID to follow it live.
          </p>
        </motion.div>

        <motion.form
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          onSubmit={(e) => {
            e.preventDefault();
            track(query);
          }}
          className="glass-panel mx-auto mt-10 flex flex-col gap-3 rounded-2xl p-3 sm:flex-row"
          data-testid="tracking-form"
        >
          <div className="flex flex-1 items-center gap-3 rounded-xl border border-line bg-input px-4">
            <PackageSearch className="h-5 w-5 shrink-0 text-amber-500" />
            <input
              data-testid="tracking-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter tracking ID"
              className="h-12 w-full bg-transparent font-mono text-sm uppercase tracking-widest text-1 placeholder:text-[var(--pq-text3)] focus:outline-none"
            />
          </div>
          <button
            data-testid="tracking-submit-btn"
            type="submit"
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-7 py-3.5 text-sm font-bold text-black transition-all duration-300 hover:bg-amber-400 hover:shadow-[0_0_28px_rgba(255,159,28,0.35)] disabled:opacity-60"
          >
            {loading ? "Locating…" : "Track Order"}
          </button>
        </motion.form>

        {samples.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2" data-testid="tracking-samples">
            <span className="text-[11px] font-medium uppercase tracking-[0.15em] text-3">Try:</span>
            {samples.map((s) => (
              <button
                key={s.tracking_id}
                data-testid={`tracking-sample-${s.tracking_id}`}
                onClick={() => {
                  setQuery(s.tracking_id);
                  track(s.tracking_id);
                }}
                className="rounded-full border border-line px-3 py-1 font-mono text-[11px] tracking-wider text-2 transition-colors hover:border-amber-500/50 hover:text-amber-400"
              >
                {s.tracking_id}
              </button>
            ))}
          </div>
        )}

        <AnimatePresence mode="wait">
          {shipment && (
            <motion.div
              key={shipment.tracking_id}
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 overflow-hidden rounded-2xl border border-line bg-surface"
              data-testid="tracking-result"
            >
              <div className="flex flex-col gap-4 border-b border-line p-6 sm:flex-row sm:items-center sm:justify-between lg:p-8">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-3">
                    PAQVERO Order
                  </p>
                  <p className="font-display mt-1 text-2xl font-semibold tracking-tight text-1" data-testid="tracking-result-id">
                    #{shipment.tracking_id}
                  </p>
                </div>
                <span
                  data-testid="tracking-result-status"
                  className={`flex w-fit items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] ${
                    shipment.status === "DELIVERED"
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                      : "border-amber-500/30 bg-amber-500/10 text-amber-400"
                  }`}
                >
                  <span className={`h-1.5 w-1.5 animate-blink rounded-full ${shipment.status === "DELIVERED" ? "bg-emerald-400" : "bg-amber-400"}`} />
                  {shipment.status}
                </span>
              </div>

              <div className="grid gap-4 border-b border-line p-6 sm:grid-cols-3 lg:p-8">
                <div className="flex items-start gap-3">
                  <Clock className="mt-0.5 h-4 w-4 text-amber-500" />
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-3">Estimated arrival</p>
                    <p className="mt-1 text-sm font-semibold text-1" data-testid="tracking-result-eta">{shipment.eta}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="mt-0.5 h-4 w-4 text-emerald-500" />
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-3">Current location</p>
                    <p className="mt-1 text-sm font-semibold text-1" data-testid="tracking-result-location">{shipment.current_location}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Truck className="mt-0.5 h-4 w-4 text-2" />
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-3">Delivery · Weight</p>
                    <p className="mt-1 text-sm font-semibold text-1">{shipment.service} · {shipment.weight}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 lg:p-8">
                <div className="relative" data-testid="tracking-timeline">
                  <div className="absolute left-0 right-0 top-[13px] h-px bg-hover" />
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
                    className="absolute left-0 top-[13px] h-px bg-gradient-to-r from-amber-500 to-emerald-500"
                  />
                  <ol className="relative flex justify-between">
                    {shipment.stages.map((stage, i) => {
                      const state = i < shipment.stage_index ? "done" : i === shipment.stage_index ? "current" : "todo";
                      return (
                        <li key={stage} className="flex w-16 flex-col items-center gap-2.5 sm:w-20" data-testid={`tracking-stage-${i}`}>
                          <span
                            className={`flex h-7 w-7 items-center justify-center rounded-full border-2 bg-surface transition-colors ${
                              state === "done"
                                ? "border-emerald-500 text-emerald-400"
                                : state === "current"
                                  ? "border-amber-500 text-amber-400"
                                  : "border-strong text-3"
                            }`}
                          >
                            {state === "done" ? (
                              <Check className="h-3.5 w-3.5" strokeWidth={3} />
                            ) : (
                              <span className={`h-1.5 w-1.5 rounded-full ${state === "current" ? "animate-blink bg-amber-400" : "bg-neutral-700"}`} />
                            )}
                          </span>
                          <span className={`text-center text-[9px] font-bold uppercase leading-tight tracking-wider sm:text-[10px] ${state === "todo" ? "text-3" : "text-1"}`}>
                            {stage}
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                </div>

                <div className="mt-8 space-y-1" data-testid="tracking-checkpoints">
                  {shipment.checkpoints
                    .filter((c) => c.state !== "upcoming")
                    .slice()
                    .reverse()
                    .map((c, i) => (
                      <div
                        key={c.stage}
                        className={`flex items-center justify-between gap-4 rounded-lg px-4 py-3 ${
                          c.state === "current" ? "border border-amber-500/20 bg-amber-500/[0.06]" : ""
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`h-1.5 w-1.5 rounded-full ${c.state === "current" ? "animate-blink bg-amber-400" : "bg-emerald-500"}`} />
                          <div>
                            <p className="text-sm font-medium text-1">{c.label || c.stage}</p>
                            {c.location && <p className="text-xs text-3">{c.location}</p>}
                          </div>
                        </div>
                        <span className="shrink-0 font-mono text-[11px] uppercase tracking-wider text-3">{c.time}</span>
                      </div>
                    ))}
                </div>

                <div className="mt-6 flex items-center justify-between rounded-xl border border-line bg-hover px-4 py-3 text-xs text-3">
                  <span>{shipment.origin}</span>
                  <span className="mx-3 hidden h-px flex-1 bg-hover sm:block" />
                  <span className="text-right text-2">{shipment.destination}</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
